import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CheckCheck, MessageCircle } from 'lucide-react';
import { EMPTY_STATES, ROLES } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useCanWrite, useRole } from '@/store/auth';
import type { DailyNote } from '@/types';

/** 알림장 목록 — 명세서 §5.3 */
export function DailyNoteListPage() {
  const role = useRole();
  const canWrite = useCanWrite();
  const isParent = role === ROLES.PARENT;

  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['daily-notes'],
      initialPageParam: undefined as string | undefined,
      queryFn: ({ pageParam }) =>
        api<DailyNote[]>(`/daily-notes?limit=15${pageParam ? `&cursor=${pageParam}` : ''}`),
      getNextPageParam: (last) => last.meta?.nextCursor ?? undefined,
    });

  const notes = data?.pages.flatMap((page) => page.data) ?? [];
  const empty = isParent ? EMPTY_STATES.dailyNoteParent : EMPTY_STATES.dailyNoteTeacher;

  return (
    <Screen>
      <AppBar title="알림장" back />

      {isPending && <Spinner />}
      {isError && <ErrorNote message="알림장을 불러오지 못했어요." onRetry={() => void refetch()} />}

      {notes.length === 0 && !isPending && (
        <EmptyState
          title={empty.title}
          body={empty.body}
          action={
            !isParent && canWrite ? (
              <Link to="/records/notes/new">
                <Button>{EMPTY_STATES.dailyNoteTeacher.action}</Button>
              </Link>
            ) : undefined
          }
        />
      )}

      <ul className="flex flex-col gap-3 p-4">
        {notes.map((note) => (
          <Card key={note.id} as="li" className="p-0">
            <Link to={`/notes/${note.id}`} className="block p-4">
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-sm font-semibold text-primary-strong">
                  {note.child?.name ?? note.classRoom?.name ?? '우리 반'}
                </span>
                <span className="text-sm text-ink-sub">{formatDate(note.noteDate)}</span>
                {note.status !== 'PUBLISHED' && (
                  <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[12px] text-ink">
                    {note.status === 'DRAFT' ? '임시저장' : '예약'}
                  </span>
                )}
                {isParent && note.readByMe && (
                  <CheckCheck size={15} className="ml-auto text-primary" aria-label="읽음" />
                )}
              </div>

              {note.title && <p className="font-semibold">{note.title}</p>}
              <p className="line-clamp-2 text-base text-ink">{note.content}</p>

              <div className="mt-2 flex items-center gap-3 text-sm text-ink-sub">
                {note.photos.length > 0 && <span>사진 {note.photos.length}장</span>}
                {note._count.comments > 0 && (
                  <span className="flex items-center gap-1">
                    <MessageCircle size={14} /> {note._count.comments}
                  </span>
                )}
                {!isParent && <span>읽음 {note._count.reads}</span>}
                {note.editedAt && <span>· 수정됨</span>}
              </div>
            </Link>
          </Card>
        ))}
      </ul>

      {hasNextPage && (
        <div className="px-4 pb-6">
          <Button
            variant="outline"
            block
            loading={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            더 보기
          </Button>
        </div>
      )}
    </Screen>
  );
}
